import React, { Component, useContext, useEffect, useState, useCallback } from 'react'
import { Button } from 'reactstrap'

import { classnames } from '../../utils/classnames'
import { Form, Checkbox, Input, Textarea } from '../partials'
import { ListDropDown } from '../partials/ListDropDown'
import {
  updateFormData
} from '../../api'

import { FieldChoices } from './FieldChoices'
import { JobAppContext } from '../../GlobalState'
import { useMappedFields } from '../../hooks'

// import Place from '../partials/Place'
import AddressField from '../partials/AddressField'
import PlaceConfigurable from '../partials/PlaceConfigurable'
import AddressConfigurator from './AddressConfigurator'
import { transformStateToDbConfig } from './addressFieldConfig'

const default_addr_form = [
	{ seq: '0', type: 'text',	  title: 'Your Customer', 	required: 1 },
	{ seq: '1', type: 'text',	  title: 'Contact Name', 	required: 0 },
	{ seq: '2', type: 'phone',	  title: 'Phone', 			required: 0 },
	{ seq: '3', type: 'text',	  title: 'Street Address', 	required: 1 },
	{ seq: '4', type: 'text', 	  title: 'Street Address', 	required: 0 },
	{ seq: '5', type: 'text', 	  title: 'City', 			required: 1 },
	{ seq: '6', type: 'dropdown', title: 'State', 			required: 0 },
	{ seq: '7', type: 'text',	  title: 'Zip', 			required: 1 },
	{ seq: '8', type: 'text',	  title: 'County', 			required: 0 },
	{ seq: '9', type: 'email',	  title: 'Email Address', 	required: 0 }
]

const defaultStyle = {
	layout: "default", // default | columns
	addr_plus: false,
	compact: false,
	fieldset: false
}

export default function Field(props) {

	const { data: mappedFieldsData } = useMappedFields();
	const mapped_types = mappedFieldsData?.mapped_types || [];
	const address_types = mappedFieldsData?.address_types || [];

	const choices = props.field.choices ?? ''

	const [ isEditing, setIsEditing ] = useState(props.field.isEditing ? true : false);
	const [ newFld, setNewFld ] = useState(props.field.new ?? false);
	const [ backup, setBackup ] = useState(props.field);

	const [ deletedChoices, setDeletedChoices ] = useState([]);
	
	const [ field, setField ] = useState({
		...props.field,
		choices: [...choices]
	});

	const [ addressModal, setAddressModal ] = useState(false);
	const [ addressConfigMode, setAddressConfigMode ] = useState('layout'); // 'layout' or 'fields'
	const [ newStyle, setNewStyle ] = useState(defaultStyle);

	useEffect(() => {
		if ( props.field?.style ) {
			// append found styles to base (default) set of "address block" styles
			try {
				setNewStyle(style => ({
					...style,
					...props.field?.style
				}))
			} catch (e) {
				console.log('error', e)
			}
		}
	}, [])
	

	const handleStyleChange = event => {
		const { target } = event
		const { name } = target
		const value = target.type === 'checkbox' ? target.checked : target.value

		// Confirm when enabling allow_same_as with existing field configs
		if (name === 'allow_same_as' && value === true && field.field_configs && field.field_configs.length > 0) {
			const confirmMessage = "Any custom field configurations from this field will be applied to the Customer address field. Do you want to continue?";
			if (!window.confirm(confirmMessage)) {
				return; // Cancel toggle
			}
		}

		// update temporary (display) style settings (what drives the preview)
		setNewStyle(style => ({ ...style, [name]: value }))

		let oStyle;
		try {
			oStyle = field.style ?? {}
		} catch (e) {
			oStyle = {}
		}
		oStyle[name] = value;

		// apply style to currently stored field settings (used for save)
		setField(field => ({ ...field, style: oStyle}))
	}

	const changeLayout = (type) => {
		
		setNewStyle(style => ({ ...style, layout: type }))
		
		let oStyle;
		try {
			oStyle = field.style ?? {}
		} catch (e) {
			oStyle = {};
		}
		oStyle['layout'] = type;

		setField(field => ({ ...field, style: oStyle }))
	}

  	const handleChange = event => {
		let { target } = event
		let { name } = target
		let value
		if (target.type === 'checkbox') {
			value = target.checked ? 1 : 0
		} else {
			value = target.value
		}

		if (name === 'type_id' && field.type == 'address') {
			value = value === '' ? null : Number(value)
		}

		setField({
			...field,
			[name]: value
		})
	}

	const addChoice = name => {
		setField({
			...field,
			choices: [
				...field.choices,
				{
					name: name,
					id: 'key_' + (field.choices.length + 1),
				},
			],
		})
	}

	// handles saves of new and existing fields
  	const handleSubmit = () => {
		setNewFld(false);
		setIsEditing(false);
		setBackup(field);

		console.log('[Field] handleSubmit - Saving field:', {
			style: field.style,
			field_configs: field.field_configs
		});

		updateFormData({
			type: 'field',
			field: field,
			form_id: props.formId,
			deletedChoices: deletedChoices,
		}).then(response => {
			console.log('[Field] handleSubmit .then - Response:', response)
			props.pushChanges(response)
			setDeletedChoices([])
		})
	}

	const cancelEdit = () => {
		if (newFld === true) {
			props.removeField(field)
		} else {
			setIsEditing(false);
			setField(backup);
		}
	}

	const handleRemoveField = () => {
		if (newFld === false) {
			if ( confirm('Are you sure you want to delete this field?') ) {
				updateFormData({
					type: 'delete_field',
					field: field,
					form_id: props.formId,
				})
				props.removeField(field)
			}
		} else {
			props.removeField(field)
		}
	}

	const toggleEdit = () => {
		setIsEditing(!isEditing);
		setField(backup);
	}

	const handleRemoveChoice = data => {
		setField({
			...field,
			choices: field.choices.filter(
				(choice, index) => data.id !== choice.id
			),
		});
		setDeletedChoices([
			...deletedChoices, 
			data.id
		]);
	}

	// const { index, onDragOver, onDragStart, onDrop, onDragLeave } = props

	const selected_type = (type, type_id) => {
		if (type != 'address') return type_id ?? 0
		return type_id > 0 ? type_id : ''
	}

	const addressTypeItems = (typeId) => {
		const items = address_types.map(item => item.id === ''
			? { ...item, name: props.isCustomerField ? 'Customer' : 'No Type ID' }
			: item)

		if (!(typeId > 0) || items.some(({ id }) => Number(id) === Number(typeId))) return items
		return [...items, { id: Number(typeId), name: String(typeId) }]
	}

	let disableField = (field.type != 'instructions' && !field.title.length) || 
						(field.type == 'instructions' && !field.description.length)

	let fieldConfig = field?.style ?? {};

	return (
		<div
			className={'field' + (isEditing ? ' editing border border-danger' : '')}
			// draggable
			// onDragOver={onDragOver}
			// onDragLeave={onDragLeave}
			// onDragStart={onDragStart}
			// onDrop={onDrop}
			id={props.id}
		>
			{field.type == 'instructions' ? (
				<>
					{isEditing ? (
						<>
							<label htmlFor='description'>Display Text <span className="required">Required</span>{' '}</label>
							<textarea
								rows={3}
								name="description"
								value={field.description}
								className="form-control"
								onChange={handleChange} />
						</>
					) : (
						<div className="staticTextField">
							{field.description ?? ''}
						</div>
					)}
				</>
			) : (
				<div style={{ flexGrow: 1 }}>
					<div className="field_data">
						
						<div className="fieldType">
							<span className="badge badge-secondary text-white">
								{field.type}
							</span>
						</div>

						<div className="field_title">
							{isEditing ? (
								<Input
									autoComplete="off"
									autoFocus={true}
									onChange={handleChange}
									name="title"
									placeholder="Field Title"
									value={field.title}
								/>
							) : (
								<div className="fieldTitleInline">
									<span>name</span>
									{ field.title || backup.title || 'Field Title' }
								</div>
							)}
						</div>

						{field.type !== 'hidden' && (
							<div className="fieldType">
								{isEditing ? (
									<Checkbox
										label="Required"
										name="required"
										value={field.required === 1}
										onChange={handleChange}
									/>
								) : (
									<>
										<span>Required</span>
										<div className="fieldTitleInline">
											{field.required ? 'yes' : 'no'}
										</div>
									</>
								)}
							</div>
						)}

						{field.type == 'text' && field.type_id == null && (
							<div className="fieldType">
								{isEditing ? (
									<>
										<Checkbox
											label="Import with Notes"
											name="add_to_notes"
											value={field.add_to_notes === 1}
											onChange={handleChange}
										/>									
									</>
								) : (
									<>
										<span>Import with Notes</span>
										<div className="fieldTitleInline">
											{field.add_to_notes == 1 ? 'yes' : 'no'}
										</div>
									</>
								)}
							</div>
						)}

						{field.type == 'date' && (
							<div className="fieldType">
								<span>Future Dates Only?</span>
								{isEditing ? (
									<input type="checkbox" name="futureDates" value="Y" checked={fieldConfig?.futureDates ?? false} onChange={handleStyleChange} />
								) : (
									<div className="fieldTitleInline">
										{fieldConfig?.futureDates ? 'yes' : 'no'}
									</div>
								)}
							</div>
						)}

						{field.type == 'multi-select' && (
							<div className="fieldType">
								<span>Style</span>
								{isEditing ? (
									<select name="display" defaultValue={fieldConfig?.display ?? 'dropdown'} onChange={handleStyleChange} className="form-control">
										<option value="dropdown">Dropdown</option>
										<option value="checkbox">Checkbox</option>
									</select>
								) : (
									<div className="fieldTitleInline">
										{fieldConfig?.display ?? 'Dropdown'}
									</div>
								)}
							</div>
						)}

						{field.type == 'address' && (
							<div className="fieldType">
								{!addressModal && (
									<>
										{isEditing ? (
											<>
												{field.type_id && field.type_id != 2 && (
													<Checkbox
														label="Allow Same As Customer"
														name="allow_same_as"
														value={fieldConfig?.allow_same_as ?? false}
														onChange={handleStyleChange}
													/>
												)}
												<div role="button" style={{ color: '#000000' }} onClick={(e) => { e.stopPropagation(); setAddressModal(addr => !addr) }}>	
													<i className="fa fa-cog mx-2"></i>
													Customize
												</div>
											</>
										) : ( 
											<>
												{field.type_id && field.type_id != 2 && (
													<div className="fieldTitleInline">
														<span>Allow Same As Customer</span>
														{fieldConfig?.allow_same_as ? 'yes' : 'no'}
													</div>
												)}
											</>
										)}
									</>
								)}
							</div>
						)}

						{/* field.type === 'hidden' && (
							<div className="field_title">
								{!isEditing && (
									<div className="fieldType">
										<span>Value</span>
										{field.defaultValue}
									</div>
								)}
							</div>
						) */}
					
					</div>

					{/* isEditing && field.type === 'hidden' && (
						<Input
							autoComplete="off"
							className="defaultValue"
							onChange={handleChange}
							name="defaultValue"
							placeholder="Default Value"
							value={field.defaultValue || ''}
						/>
					) */}

					{isEditing && !addressModal && (
						<>
							<Input
								autoComplete="off"
								className="description"
								onChange={handleChange}
								name="description"
								placeholder="Description"
								value={field.description ?? ''}
							/>
							<ListDropDown
								label="Type ID"
								description='Required for "mapped" and "address" fields'
								className="form-group input-wrapper"
								name="type_id"
								placeholder={false}
								selected={selected_type(field.type, field.type_id)}
								onChange={handleChange}
								items={field.type == 'address' ? addressTypeItems(field.type_id) : mapped_types}
							/>
							{(['choice', 'assoc_choice', 'multi-select'].indexOf(field.type) >= 0) && (
								<FieldChoices
									choices={field.choices}
									addChoice={addChoice}
									removeChoice={handleRemoveChoice}
								/>
							)}
						</>
					)}

					{isEditing && addressModal && (
						<div className="row p-3 mt-3">
							<div className="col-12">
								{/* Configurator Mode Tabs */}
								<ul className="nav nav-tabs mb-3">
									<li className="nav-item">
										<a 
											className={`nav-link ${addressConfigMode === 'layout' ? 'active' : ''}`}
											onClick={() => setAddressConfigMode('layout')}
											style={{ cursor: 'pointer' }}
										>
											<i className="fa fa-th-large mr-2" />
											Layout & Style
										</a>
									</li>
									<li className="nav-item">
										<a 
											className={`nav-link ${addressConfigMode === 'fields' ? 'active' : ''}`}
											onClick={() => setAddressConfigMode('fields')}
											style={{ cursor: 'pointer' }}
										>
											<i className="fa fa-list mr-2" />
											Field Configuration
										</a>
									</li>
								</ul>

								{addressConfigMode === 'layout' ? (
									<div className="row">
										<div className="col-4">
											<h5 className="border-bottom pb-3 my-3">Customize Address Form Layout</h5>
											<ul className="list-unstyled d-flex align-items-center">
												<li className={"mr-3 p-3 border rounded-pill" + (newStyle.layout == 'default' ? ' border-dark' : ' border-white list-link')}
													onClick={() => changeLayout("default")}>Default</li>
												<li className={"p-3 border rounded-pill" + (newStyle.layout == 'columns' ? ' border-dark' : ' border-white list-link')}
													onClick={() => changeLayout("columns")}>Columns</li>
											</ul>
											<div>
												<input type="checkbox" name="addr_plus" value="Y" checked={newStyle?.addr_plus} onChange={handleStyleChange} /> Lot, Parcel, &amp; Block<br />
												<input type="checkbox" name="compact" value="Y" checked={newStyle?.compact} onChange={handleStyleChange} /> Compact<br />
												<input type="checkbox" name="fieldset" value="Y" checked={newStyle?.fieldset} onChange={handleStyleChange} /> Fieldset<br />
											</div>
											<div className="mt-3">
												<button type="button" className="btn btn-primary" onClick={handleSubmit}>Save</button>
												<button type="button" className="btn btn-secondary ml-3" onClick={(e) => { e.stopPropagation(); setNewStyle(defaultStyle); setAddressModal(addr => !addr) }}>Cancel</button>
											</div>
										</div>
										<div className="col-8">
											<h5 className="border-bottom pb-3 my-3">Preview</h5>
											{field.field_configs && field.field_configs.length > 0 ? (
												<PlaceConfigurable
													fldType={field.type}
													description={field.description}
													hasContact={field.type_id != 2 ? true : false}
													isRequired={field.required}
													label={field.title + ' '}
													onChange={() => {}} // No-op for preview
													updateAnswers={() => {}} // No-op for preview
													value={{ address: {} }} // Empty value for preview
													parent={field.id}
													type_id={field.type_id}
													allow_none={false}
													readOnly={false}
													fieldConfigs={field.field_configs}
													style={newStyle}
												/>
											) : (
												<AddressField
													fldType={field.type}
													description={field.description}
													hasContact={field.type_id != 2 ? true : false}
													isRequired={field.required}
													label={field.title + ' '}
													onChange={() => {}} // No-op for preview
													updateAnswers={() => {}} // No-op for preview
													value={{ address: {} }} // Empty value for preview
													parent={field.id}
													type_id={field.type_id}
													allow_none={false}
													readOnly={false}
													fieldConfigs={field.field_configs}
													style={newStyle}
												/>
												// <Place
												// 	fldType={field.type}
												// 	description={field.description}
												// 	hasContact={field.type_id != 2 ? true : false}
												// 	isRequired={field.required}
												// 	label={field.title + ' '}
												// 	onChange={handleChange}
												// 	parent={field.id}
												// 	type_id={field.type_id}
												// 	allow_none={false}
												// 	readOnly={false}
												// 	fields={field.field_configs}
												// 	style={newStyle}
												// />
											)}
										</div>
									</div>
								) : (
									<div className="row">
										<div className="col-12">
											<AddressConfigurator
												typeId={field.type_id}
												fieldConfigs={field.field_configs || []}
												onChange={(config) => {
													console.log('[AddressConfigurator] onChange called with config:', config);
													// Transform to database format and store in field_configs ONLY
													// DO NOT touch field.style - that's for layout options only
													const dbConfigs = transformStateToDbConfig(config.fields, field.id);
													console.log('[AddressConfigurator] Transformed to dbConfigs:', dbConfigs);
													setField(prevField => {
														console.log('[AddressConfigurator] Previous field state:', {
															style: prevField.style,
															field_configs: prevField.field_configs
														});
														return { 
															...prevField, 
															field_configs: dbConfigs 
														};
													});
												}}
											onSave={(config) => {
												// Check if allow_same_as is enabled before saving
												if (field.style?.allow_same_as) {
													const confirmMessage = "These field configurations will also be applied to the Customer address field, since 'Allow Same As Customer' is enabled. Do you want to continue?";
													if (!window.confirm(confirmMessage)) {
														return; // Cancel save
													}
												}
												
												// Transform to database format and save to field_configs
												// field.style remains unchanged (contains layout, addr_plus, compact, fieldset)
												const dbConfigs = transformStateToDbConfig(config.fields, field.id);
												setField(prevField => ({ 
													...prevField, 
													field_configs: dbConfigs 
												}));
												handleSubmit();
											}}
												onCancel={() => {
													setAddressModal(false);
												}}
											/>
										</div>
									</div>
								)}
							</div>
						</div>
					)}
				</div>
			)}
			{isEditing ? (
				<>
				{!addressModal && (
					<Form className={classnames('fieldOptions')} onSubmit={e => e.preventDefault()}>
						<Button
							color="primary"
							onClick={handleSubmit}
							disabled={disableField}
							type="submit"
						>
							Save
						</Button>
						<Button onClick={cancelEdit}>
							Cancel
						</Button>
					</Form>
				)}
				</>
			) : (
				<div className="editButtons">
					<span className="fa fa-pencil-alt mr-3" onClick={toggleEdit} style={{ cursor: 'pointer' }} />
					<span className="fa fa-trash" onClick={handleRemoveField} style={{ cursor: 'pointer' }} />
				</div>	
			)}
		</div>
	)
}
